<template>
  <div id="app" v-if="appVisible" :data-embed-mode="embedMode">
    <a class="skip-link" href="#page-container">Skip to content</a>
    <b-navbar v-if="!embedChromeHidden" toggleable="md" type="dark" class="navbar navbar-expand-md navbar-dark bg-primary" :class="{ 'preview-navbar': isPreviewMode }">

      <b-navbar-toggle v-if="showMenuToggle" target="nav_collapse"></b-navbar-toggle>

      <b-navbar-brand :to="{ path: `/${$route.params.application || 'TURBO'}` }">
        <img v-if="customLogo" class="app-logo" :src="customLogo" alt="">
        {{appName}}
      </b-navbar-brand>

      <b-collapse is-nav id="nav_collapse">

        <b-navbar-nav>
          <MenuEntries
            :items="leftMenuItems"
            :depth="1"
            :application="$route.params.application"
            :current-page="uniqueTitle"
            :value-text="(item) => menuValueText(item)"
            :children-of="(item) => visibleChildren(item)"
          />
        </b-navbar-nav>

        <b-navbar-nav class="ms-auto" v-if="rightMenuItems.length || loggedInUser">
          <MenuEntries
            :items="rightMenuItems"
            :depth="1"
            :application="$route.params.application"
            :current-page="uniqueTitle"
            :value-text="(item) => menuValueText(item)"
            :children-of="(item) => visibleChildren(item)"
          />

          <b-nav-item-dropdown v-if="loggedInUser" class="navbar-account" right :aria-label="`Account menu for ${loggedInUser}`">
            <template #button-content>
              <em>{{loggedInUser}}</em>
            </template>
            <b-dropdown-item-button @click="signout()">Signout</b-dropdown-item-button>
          </b-nav-item-dropdown>
        </b-navbar-nav>

      </b-collapse>
    </b-navbar>

    <div v-if="oauthFactor" class="oauth-factor-panel">
      <AuthenticatorPanel
        :phase="oauthFactor.phase"
        :otpauth-url="oauthFactor.otpauthUrl"
        :secret="oauthFactor.secret"
        :recovery-codes="oauthFactor.recoveryCodes"
        :error-message="oauthFactor.error"
        :busy="oauthFactor.busy"
        @submit="submitOAuthFactor"
        @continue="finishOAuthFactor"
      />
    </div>

    <div v-if="sessionNotice" class="alert alert-warning session-notice" role="status">
      <span>{{ sessionNotice }}</span>
      <button type="button" class="btn btn-sm btn-outline-dark" @click="sessionNotice = ''">Dismiss</button>
    </div>

    <main class="container-fluid" id="page-container" :page-unique-title="uniqueTitle" :data-page-id="uniqueTitle">
      <p v-if="embedNotice" class="embed-notice" role="status">{{ embedNotice }}</p>
      <nav v-if="breadcrumbItems.length > 0 && embedMode !== 'view'" aria-label="breadcrumb">
        <ol class="superturbo-breadcrumbs">
          <li v-for="(item, idx) in breadcrumbItems" :key="idx" class="breadcrumb-item">
            <router-link
              v-if="idx < breadcrumbItems.length - 1"
              :to="{ path: item.path }"
              class="breadcrumb-link"
            >{{ item.label }}</router-link>
            <span v-else class="breadcrumb-current" aria-current="page">{{ item.label }}</span>
          </li>
        </ol>
      </nav>
      <div v-if="loginRequired" class="access-gate text-center mt-5">
        <h1>Login required</h1>
        <p>You must be logged in to view this page.</p>
      </div>
      <div v-else-if="permissionDenied" class="access-gate text-center mt-5">
        <h1>No access</h1>
        <p>Your account cannot view this page.</p>
      </div>
      <PasswordReset v-if="passwordReset" />
      <template v-else-if="!loginRequired && !permissionDenied">
        <PageOpenBanners :key="uniqueTitle" :banners="pageOpenBanners" />
        <PageComponents :components="components" />
      </template>
    </main>
    <PageModal
      v-if="modalOpen && modalTarget"
      :target="modalTarget"
      :account="routeAccount"
      :application="$route.params.application"
      @close="closePageModal"
    />
    <ImageLightbox />
  </div>
</template>

<script>

import axios from 'axios'

import PageComponents from './components/PageComponents.vue'
import PageOpenBanners from './components/PageOpenBanners.vue'
import PageModal from './components/PageModal.vue'
import AuthenticatorPanel from './components/AuthenticatorPanel.vue'
import PasswordReset from './components/PasswordReset.vue'
import { instantiatePageComponent } from './lib/page-components'

import themes from './lib/themes.js'
import ImageLightbox from './components/ImageLightbox.vue'
import MenuEntries from './components/MenuEntries.vue'
import menuItemsOnSide from './menu-align.js'
import { shouldRenderView } from './lib/view-visibility'
import { clearLoggedInUser, readLoggedInUser, writeLoggedInUser } from './lib/live-user-storage'
import { sessionProbeResult } from '../../shared/live-user-session'
import { setAppTimeZone } from './lib/app-time-zone'
import { beginPageLoad, pageLoadIsCurrent } from '../../shared/page-load'
import { presentLivePage } from '../../shared/page-open-rules'
import { pageRequestUrl, openPageRedirect } from './lib/page-open'
import { modalNavigationTarget, normalizeAppPath } from '../../shared/page-modal'
import { embedViewPresentation } from '../../shared/embed'
import { scopeCss } from '../../shared/view-css'
import { runCustomCodeInPage } from './lib/run-custom-code'
import {
  userSessionForRead,
  createSessionWatch,
  sessionEndedMessage,
  sessionWarningMessage,
  SESSION_TOUCH_GAP_MS
} from '../../shared/user-session-policy'

let currentPageUniqueTitle = ''

window.__superturbo_getPageUniqueTitle = () => currentPageUniqueTitle

export default {
  props: ['pageUnqiueTitle'],
  name: 'app',
  components: {
    PageComponents,
    PageOpenBanners,
    PageModal,
    AuthenticatorPanel,
    PasswordReset,
    ImageLightbox,
    MenuEntries
  },
  watch: {
    async '$route' (to, from) {

      if (this.isPreviewMode || this.accountLocked) return

      if (to.path === from.path) {

        return
      }

      // Reload menu and theme when the application changes
      if (to.params.application !== from.params.application) {

        this.loggedInUser = readLoggedInUser({
          account: this.routeAccount,
          application: to.params.application
        })
        this.loadMenu()
        this.loadModalPages()
      }

      if (this.modalOpen) this.closePageModal()

      this.components = []

      await this.loadTheme()
      await this.loadComponents()
      this.loadMenuValues()
    }
  },
  computed: {
    visibleMenuItems () {

      return this.menuItems.filter(item => {

        if (item.requiresLogin && !this.loggedInUser) return false
        if (item.hideWhenLoggedIn && this.loggedInUser) return false

        // Hide dropdown if all its visible children are filtered out
        if (item.type === 'dropdown' && item.children) {

          const visible = this.visibleChildren(item)

          if (visible.length === 0) return false
        }

        return true
      })
    },
    leftMenuItems () {

      return menuItemsOnSide(this.visibleMenuItems, 'left')
    },
    rightMenuItems () {

      return menuItemsOnSide(this.visibleMenuItems, 'right')
    },
    showMenuToggle () {

      return this.visibleMenuItems.length > 0 || !!this.loggedInUser
    },
    embedMode () {

      return (this.routeEmbed && this.routeEmbed.mode) || 'none'
    },
    embedChromeHidden () {

      return this.embedMode === 'page' || this.embedMode === 'view'
    }
  },
  methods: {
    menuValueText (item) {

      const entry = this.menuValues[item.id]

      if (!entry || entry.display === '' || entry.display == null) return '—'

      return entry.display
    },
    visibleChildren (item) {

      if (!item.children) return []

      return item.children.filter(child => {

        if (child.requiresLogin && !this.loggedInUser) return false
        if (child.hideWhenLoggedIn && this.loggedInUser) return false
        return true
      })
    },
    async loadComponents () {

      const loadId = beginPageLoad(this.pageLoadSeq)
      this.pageLoadSeq = loadId

      console.log(`loadComponents`)
      console.log(this.routeAccessKey)
      console.log(this.routePageUniqueTitle)
      console.log(this.$route.params.application)
      console.log(this.routeAccount)

      this.permissionDenied = false
      this.pageOpenBanners = []
      this.embedNotice = ''
      this.passwordReset = this.routePageUniqueTitle === 'reset-password'

      if (this.passwordReset) {

        this.loginRequired = false
        this.components = []
        this.pageTitle = 'Reset password'
        currentPageUniqueTitle = 'reset-password'
        this.appVisible = true

        return
      }

      let page
      const pageUrl = pageRequestUrl(this.routePageUniqueTitle
        ? `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/pages/${this.routePageUniqueTitle}`
        : `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/homepage`, this.routeAccessKey)

      let pageData

      try {

        pageData = await axios.get(pageUrl)
      } catch (error) {

        if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return

        const data = error.response && error.response.data
        const denied = error.response && error.response.status === 403 && data && data.permissionDenied
        const loginRequired = error.response && error.response.status === 401 && data && data.loginRequired

        if (loginRequired) {

          this.loginRequired = true
          this.permissionDenied = false
          this.components = []
          return
        }

        if (!denied) throw error

        this.permissionDenied = true
        this.loginRequired = false
        this.components = []
        return
      }

      if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return

      page = pageData.data

      if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return

      this.uniqueTitle = page.uniqueTitle
      currentPageUniqueTitle = page.uniqueTitle
      this.pageTitle = page.title || ''
      this.syncDocumentTitle()

      this.breadcrumbItems = []
      const breadcrumbLoadId = ++this.breadcrumbLoadSeq
      const showBreadcrumbs = page.showBreadcrumbs === true || page.showBreadcrumbs === 'true'

      if (showBreadcrumbs && this.routeAllPathSegments && this.routeAllPathSegments.length > 0) {
        this.loadBreadcrumbs(breadcrumbLoadId)
      }

      console.log('page is')
      console.log(page)

      if (page.requiresLogin && !this.loggedInUser) {

        console.log(`page requires login, user not logged in`)
        this.loginRequired = true

        return
      }

      this.loginRequired = false

      if (openPageRedirect(this.$router, this.$route.params.application, page)) return

      this.pageOpenBanners = (page.pageOpen && page.pageOpen.banners) || []
      this.components = []

      console.log('about to iterate over page components')
      console.log(page.components)

      if (!page.components) {

        return
      }

      // Inject custom CSS for all components on this page
      this.injectAllCustomCss(page)

      if (this.embedMode === 'view') {

        this.loadEmbedView(page, loadId)
        return
      }

      for (let component of page.components) {

        if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return
        if (!shouldRenderView(component)) continue

        this.loadComponent(component)
      }

      // Component styles are added while the views mount. Move the custom
      // stylesheet to the end so it overrides those rules.
      this.$nextTick(() => {
        if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return
        this.injectAllCustomCss(page)
      })
    },
    loadEmbedView (page, loadId) {

      const outcome = embedViewPresentation(
        page.components,
        this.routeEmbed && this.routeEmbed.viewUniqueTitle,
        !!this.loggedInUser
      )

      if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return

      const finish = () => {

        this.$nextTick(() => {

          if (!pageLoadIsCurrent(this.pageLoadSeq, loadId)) return
          this.injectAllCustomCss(page)
        })
      }

      if (outcome.missing) {

        this.embedNotice = 'This view is not available.'
        finish()
        return
      }

      if (outcome.hidden) {

        this.embedNotice = 'This view is hidden while you are signed in.'
        finish()
        return
      }

      if (outcome.loginRequired) {

        if (!outcome.loginComponent) {

          this.loginRequired = true
          finish()
          return
        }

        this.embedNotice = 'Sign in to continue.'
        this.loadComponent(outcome.loginComponent)
        finish()
        return
      }

      if (outcome.component) this.loadComponent(outcome.component)
      finish()
    },
    loadComponent (component) {

      const instantiatedComponent = instantiatePageComponent(component, {
        pageUniqueTitle: this.uniqueTitle || '',
        accessKey: this.routeAccessKey
      })

      if (instantiatedComponent) this.components.push(instantiatedComponent)
    },
    async loadBreadcrumbs (breadcrumbLoadId) {

      try {

        const account = this.routeAccount
        const application = this.$route.params.application
        const segments = this.routeAllPathSegments.map(s => encodeURIComponent(s)).join(',')

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/breadcrumbs?segments=${segments}`)

        if (breadcrumbLoadId !== this.breadcrumbLoadSeq) return

        this.breadcrumbItems = Array.isArray(res.data) ? res.data : []
      } catch (err) {

        console.log('failed to load breadcrumbs', err)

        if (breadcrumbLoadId !== this.breadcrumbLoadSeq) return

        this.breadcrumbItems = []
      }
    },
    async loadTheme () {

      try {

        const account = this.routeAccount
        const application = this.$route.params.application

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/settings`)

        const themeId = res.data && res.data.theme ? res.data.theme : 'default'
        setAppTimeZone(res.data && res.data.timeZone)
        this.userSession = userSessionForRead(res.data && res.data.userSession)
        await this.startSessionWatch()

        // Remove any previously injected theme link
        const existing = document.getElementById('superturbo-theme')

        if (existing) {

          existing.remove()
        }

        let cssUrl

        if (themeId.startsWith('custom-')) {

          // Custom theme: serve CSS from the app server
          cssUrl = `${VUE_APP_PROXY_SERVER_URL}/custom-themes/${application}/${themeId}/style.css`
        } else {

          // Built-in theme: look up CDN URL from static array
          const theme = themes.find(t => t.id === themeId)

          if (!theme || !theme.css) {

            return
          }

          cssUrl = theme.css
        }

        const link = document.createElement('link')
        link.id = 'superturbo-theme'
        link.rel = 'stylesheet'
        link.href = cssUrl
        const customStyle = [...document.head.children].find(el =>
          el.id === 'app-custom-css' ||
          el.id.startsWith('page-css-') ||
          el.id.startsWith('view-css-')
        )
        if (customStyle) document.head.insertBefore(link, customStyle)
        else document.head.appendChild(link)
      } catch (err) {

        console.log('failed to load theme settings', err)
      }
    },
    async signout ({ reason, revoke = true } = {}) {

      if (this.signingOut) return

      this.signingOut = true
      this.stopSessionWatch()

      const notice = sessionEndedMessage(reason)

      if (notice) this.sessionNotice = notice

      const account = this.routeAccount
      const application = this.$route.params.application

      clearLoggedInUser({ account, application })
      this.loggedInUser = ``

      try {

        // The page reload must see the session already closed, or a protected
        // page stays on "No access" instead of asking the visitor to sign in.
        if (revoke && application) {

          await axios.post(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/logout`, {}).catch(() => {})
        }
      } finally {

        this.loadMenu()
        this.loadMenuValues()
        this.reloadPageForLoginState()
        this.signingOut = false
      }
    },
    async startSessionWatch () {

      this.stopSessionWatch()

      if (this.isPreviewMode) return

      const application = this.$route.params.application

      if (!application) return

      const scope = { account: this.routeAccount, application }
      let expiresAt

      try {

        const open = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${application}/session`)
        const probe = sessionProbeResult(open.status, open.data)

        if (probe.username) {

          writeLoggedInUser(probe.username, scope)
          this.loggedInUser = probe.username
        }

        expiresAt = probe.expiresAt
      } catch (err) {

        const probe = sessionProbeResult(err.response && err.response.status, err.response && err.response.data)

        if (probe.signedOut) {

          clearLoggedInUser(scope)
          this.loggedInUser = ``

          if (probe.sessionEnded) this.sessionNotice = sessionEndedMessage(probe.reason)
        }

        return
      }

      if (!expiresAt || !this.loggedInUser) return

      const policy = userSessionForRead(this.userSession)

      const watch = createSessionWatch({
        inactivityMinutes: policy.inactivityMinutes,
        expiresAt,
        onWarning: () => {

          this.sessionWarning = true
          this.sessionNotice = sessionWarningMessage()
        },
        onIdle: () => this.signout({ reason: 'idle' }),
        onExpired: () => this.signout({ reason: 'expired' }),
        onTouch: () => this.checkSession(true)
      })

      this.sessionWatch = watch

      if (watch.expired) {

        this.signout({ reason: 'expired' })

        return
      }

      if (policy.inactivityMinutes) {

        this.sessionActivity = () => {

          if (this.sessionWarning) {

            this.sessionWarning = false
            if (this.sessionNotice === sessionWarningMessage()) this.sessionNotice = ''
          }

          if (this.sessionWatch) this.sessionWatch.noteActivity()
        }

        window.addEventListener('pointerdown', this.sessionActivity)
        window.addEventListener('keydown', this.sessionActivity)
        window.addEventListener('wheel', this.sessionActivity, { passive: true })
      }

      this.sessionPollTimer = setInterval(() => this.checkSession(false), SESSION_TOUCH_GAP_MS)
      this.checkSession(false)
    },
    stopSessionWatch () {

      if (this.sessionWatch) {

        this.sessionWatch.stop()
        this.sessionWatch = null
      }

      if (this.sessionPollTimer) {

        clearInterval(this.sessionPollTimer)
        this.sessionPollTimer = null
      }

      if (this.sessionActivity) {

        window.removeEventListener('pointerdown', this.sessionActivity)
        window.removeEventListener('keydown', this.sessionActivity)
        window.removeEventListener('wheel', this.sessionActivity)
        this.sessionActivity = null
      }
    },
    async checkSession (touch) {

      if (this.signingOut || this.isPreviewMode) return

      const application = this.$route.params.application

      if (!this.loggedInUser || !application) return

      try {

        const touchQuery = touch ? '?touch=1' : ''

        await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${application}/session${touchQuery}`)
      } catch (err) {

        const data = err.response && err.response.data

        if (data && data.sessionEnded) this.signout({ reason: data.reason, revoke: false })
        else if (err.response && err.response.status === 401) this.signout({ revoke: false })
      }
    },
    reloadPageForLoginState () {

      if (this.isPreviewMode) return

      this.components = []
      this.loadComponents()
    },
    onPreviewMessage (event) {
      if (!event.data) return

      if (event.data.type === 'superturbo-preview-ping') {

        if (window.parent !== window) {

          window.parent.postMessage({ type: 'superturbo-preview-ready' }, '*')
        }

        return
      }

      if (event.data.type === 'superturbo-preview-page') {
        const page = event.data.page
        if (!page) return

        this.components = []
        this.uniqueTitle = page.uniqueTitle || 'preview'
        currentPageUniqueTitle = page.uniqueTitle || 'preview'
        this.pageTitle = page.title || ''
        this.syncDocumentTitle()
        this.loginRequired = false
        this.pageOpenBanners = []

        const presented = presentLivePage(page, {})

        this.pageOpenBanners = (presented.pageOpen && presented.pageOpen.banners) || []

        // Inject any customCss from the page components
        this.injectAllCustomCss(presented)

        if (!presented.components || presented.components.length === 0) return

        for (let component of presented.components) {
          if (!shouldRenderView(component)) continue
          this.loadComponent(component)
        }
        this.$nextTick(() => this.injectAllCustomCss(presented))
        return
      }

      if (event.data.type === 'superturbo-preview-css') {
        this.injectCustomCss(
          event.data.componentUniqueTitle,
          event.data.css
        )
        return
      }

      if (event.data.type === 'superturbo-preview-page-css') {
        this.injectPageCss(
          event.data.pageUniqueTitle,
          event.data.css
        )
        return
      }

      if (event.data.type === 'superturbo-preview-app-css') {
        this.injectAppCss(event.data.css)
        return
      }
    },
    injectCustomCss (componentUniqueTitle, cssText) {
      const styleId = `view-css-${componentUniqueTitle}`
      let styleEl = document.getElementById(styleId)

      if (!cssText || !cssText.trim()) {
        if (styleEl) styleEl.remove()
        return
      }

      const scopedCss = scopeCss(`[data-view-id="${componentUniqueTitle}"]`, cssText)

      if (!styleEl) {
        styleEl = document.createElement('style')
        styleEl.id = styleId
      }

      styleEl.textContent = scopedCss
      document.head.appendChild(styleEl)
    },
    injectPageCss (pageUniqueTitle, cssText) {
      const styleId = `page-css-${pageUniqueTitle}`
      let styleEl = document.getElementById(styleId)

      if (!cssText || !cssText.trim()) {
        if (styleEl) styleEl.remove()
        return
      }

      const scopedCss = scopeCss(`[data-page-id="${pageUniqueTitle}"]`, cssText)

      if (!styleEl) {
        styleEl = document.createElement('style')
        styleEl.id = styleId
      }

      styleEl.textContent = scopedCss
      document.head.appendChild(styleEl)
    },
    injectAllCustomCss (page) {
      // Remove all existing view-css and page-css style elements
      document.querySelectorAll('style[id^="view-css-"]').forEach(el => el.remove())
      document.querySelectorAll('style[id^="page-css-"]').forEach(el => el.remove())

      // Inject page-level custom CSS
      if (page.customCss && page.uniqueTitle) {
        this.injectPageCss(page.uniqueTitle, page.customCss)
      }

      // Inject view-level custom CSS
      const walkComponents = (components) => {
        if (!components) return
        for (const comp of components) {
          if (comp.customCss) {
            this.injectCustomCss(comp.uniqueTitle, comp.customCss)
          }
          if (comp.components) {
            walkComponents(comp.components)
          }
        }
      }

      walkComponents(page.components)
    },
    injectAppCss (cssText) {
      const styleId = 'app-custom-css'
      let styleEl = document.getElementById(styleId)

      if (!cssText || !cssText.trim()) {
        if (styleEl) styleEl.remove()
        return
      }

      if (!styleEl) {
        styleEl = document.createElement('style')
        styleEl.id = styleId
      }

      styleEl.textContent = cssText
      document.head.appendChild(styleEl)
    },
    async preparePreview () {

      await this.loadCustomCode()
      window.addEventListener('message', this.onPreviewMessage)

      if (window.parent !== window) {

        window.parent.postMessage({ type: 'superturbo-preview-ready' }, '*')
      }
    },
    async loadCustomCode () {

      if (this.customCodeLoaded) return

      this.customCodeLoaded = true

      try {

        const account = this.routeAccount
        const application = this.$route.params.application

        if (!application) return

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/settings`)

        await runCustomCodeInPage(res.data && res.data.customCode)
      } catch (err) {

        console.log('failed to load custom code', err)
      }
    },
    async loadAppCss () {
      try {
        const account = this.routeAccount
        const application = this.$route.params.application

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/settings`)

        if (res.data && res.data.customCss) {
          this.injectAppCss(res.data.customCss)
        }
      } catch (err) {
        console.log('failed to load app custom css', err)
      }
    },
    syncDocumentTitle () {

      const page = String(this.pageTitle || '').trim()
      const app = String(this.appName || 'TurboHammer').trim() || 'TurboHammer'

      document.title = page ? `${page} · ${app}` : app
    },
    async loadMenu () {

      try {

        const account = this.routeAccount
        const application = this.$route.params.application

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/settings`)

        if (res.data && res.data.menuConfig && res.data.menuConfig.items) {

          this.menuItems = res.data.menuConfig.items
        }

        if (res.data && res.data.logoBase64) {

          this.customLogo = res.data.logoBase64
        } else {

          this.customLogo = null
        }

        if (res.data && res.data.appDisplayName) {

          this.appName = res.data.appDisplayName
        } else {

          this.appName = 'TurboHammer'
        }

        this.syncDocumentTitle()

        await this.loadMenuValues()
      } catch (err) {

        console.log('failed to load menu config', err)
      }
    },
    async loadMenuValues () {

      const hasValueItem = (items) => (items || []).some(item => item.type === 'value' || hasValueItem(item.children))
      const hasValue = hasValueItem(this.menuItems)

      if (!hasValue) {

        this.menuValues = {}

        return
      }

      try {

        const account = this.routeAccount
        const application = this.$route.params.application
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/menu-values`)

        this.menuValues = (res.data && res.data.values) || {}
      } catch (err) {

        console.log('failed to load menu values', err)
      }
    },
    async loadModalPages () {

      try {

        const account = this.routeAccount
        const application = this.$route.params.application
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/modal-pages`)
        const data = res.data || {}

        this.modalUniqueTitles = Array.isArray(data.uniqueTitles) ? data.uniqueTitles : []
        this.modalHomeUniqueTitle = data.homeUniqueTitle || ''
      } catch (err) {

        console.log('failed to load modal pages', err)
        this.modalUniqueTitles = []
        this.modalHomeUniqueTitle = ''
      }
    },
    offerModalNavigation (path) {

      if (this.isPreviewMode || this.accountLocked || this.embedMode === 'view') return false

      const target = modalNavigationTarget({
        path,
        application: this.$route.params.application,
        modalUniqueTitles: this.modalUniqueTitles,
        homeUniqueTitle: this.modalHomeUniqueTitle,
        currentPageUniqueTitle: this.uniqueTitle
      })

      if (!target) return false

      if (this.modalOpen) this.modalRedirected = true

      this.openPageModal(target)

      return true
    },
    onModalNavigation (to, from) {

      if (this.isPreviewMode || this.accountLocked) return false
      if (!from || !from.matched || !from.matched.length) return false

      if (normalizeAppPath(to.path) === normalizeAppPath(from.path)) {

        if (!this.modalOpen) return false

        const sameTarget = to.fullPath === from.fullPath

        this.closePageModal()

        return sameTarget
      }

      return this.offerModalNavigation(to.path)
    },
    onDocumentLinkClick (event) {

      if (this.isPreviewMode || this.accountLocked) return
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return

      const origin = event.target && event.target.nodeType === 1
        ? event.target
        : event.target && event.target.parentElement
      const link = origin && origin.closest ? origin.closest('a[href]') : null

      if (!link) return

      const targetAttr = link.getAttribute('target')

      if ((targetAttr && targetAttr !== '_self') || link.hasAttribute('download')) return

      const href = link.getAttribute('href')

      if (!href || href.startsWith('#')) return

      let url

      try {

        url = new URL(href, window.location.href)
      } catch (err) {

        return
      }

      if (url.origin !== window.location.origin) return

      const path = normalizeAppPath(url.pathname)

      if (this.modalOpen && path === normalizeAppPath(window.location.pathname)) {

        const next = `${path}${url.search}${url.hash}`
        const current = `${normalizeAppPath(window.location.pathname)}${window.location.search}${window.location.hash}`

        this.closePageModal()

        if (next === current) {

          event.preventDefault()
          event.stopPropagation()
        }

        return
      }

      if (!this.offerModalNavigation(path)) return

      event.preventDefault()
      event.stopPropagation()
    },
    openPageModal (target) {

      if (!this.modalOpen) this.modalOpener = document.activeElement

      this.modalTarget = {
        pageUniqueTitle: target.pageUniqueTitle,
        accessKey: target.accessKey,
        segments: target.segments.slice(),
        path: target.path
      }
      this.modalOpen = true
      document.body.style.overflow = 'hidden'
    },
    closePageModal () {

      if (!this.modalOpen) return

      this.modalOpen = false
      this.modalTarget = null
      this.modalRedirected = false
      document.body.style.overflow = ''

      const opener = this.modalOpener

      this.modalOpener = null
      this.$nextTick(() => {

        if (opener && typeof opener.focus === 'function') opener.focus()
      })
    },
    reloadCurrentPage () {

      window.dispatchEvent(new CustomEvent('superturbo-reload-page'))
      this.loadMenu()
      this.loadMenuValues()
      this.loadComponents()
    },
    async submitOAuthFactor (code) {

      if (!this.oauthFactor || this.oauthFactor.busy) return

      this.oauthFactor.busy = true
      this.oauthFactor.error = ''

      const setup = this.oauthFactor.phase === 'setup'
      const account = this.routeAccount
      const application = this.$route.params.application
      const path = setup ? 'login/authenticator/setup' : 'login/authenticator'
      const body = setup
        ? { setupToken: this.oauthFactor.setupToken, code }
        : { challengeToken: this.oauthFactor.challengeToken, code }

      try {

        const response = await axios.post(
          `${VUE_APP_PROXY_SERVER_URL}/${account || 'default'}/${application}/${path}`,
          body
        )

        if (response.data && response.data.recoveryCodes && response.data.recoveryCodes.length) {

          this.oauthFactor.phase = 'recovery'
          this.oauthFactor.recoveryCodes = response.data.recoveryCodes
          this.oauthFactor.username = response.data.username
          this.oauthFactor.userSession = response.data.userSession
          this.oauthFactor.busy = false

          return
        }

        this.finishOAuthSession(response.data)
      } catch (error) {

        this.oauthFactor.busy = false
        const data = error && error.response && error.response.data

        this.oauthFactor.error = (data && data.error) || 'That code was not accepted.'
      }
    },
    finishOAuthFactor () {

      if (!this.oauthFactor) return

      this.finishOAuthSession({
        username: this.oauthFactor.username,
        userSession: this.oauthFactor.userSession
      })
    },
    finishOAuthSession (data) {

      if (!data || !data.username) return

      writeLoggedInUser(data.username, {
        account: this.routeAccount,
        application: this.$route.params.application
      })
      this.loggedInUser = data.username
      this.oauthFactor = null

      window.dispatchEvent(new CustomEvent('superturbo-login', {
        detail: {
          username: data.username,
          userSession: data.userSession
        }
      }))
    },
    async readAccountLock () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/account-lock`, { timeout: 4000 })
        return !!(res.data && res.data.locked)
      } catch (err) {
        return false
      }
    },
    async initApp () {
      if (await this.readAccountLock()) {
        this.accountLocked = true
        this.appVisible = false
        return
      }
      this.appVisible = true
      await this.handleOAuthCallback()
      await Promise.all([this.loadTheme(), this.loadModalPages(), this.loadCustomCode()])
      this.loadMenu()
      this.loadAppCss()
      this.loadComponents()
    },
    async handleOAuthCallback () {

      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      const stateParam = urlParams.get('state')

      if (!code || !stateParam) return

      const account = this.routeAccount
      const application = this.$route.params.application

      if (!application) return

      try {
        const response = await axios.post(
          `${VUE_APP_PROXY_SERVER_URL}/${account || 'default'}/${application}/oauth/token-exchange`,
          { code, state: stateParam }
        )

        const returnPath = response.data && response.data.returnPath
        const nextPath = returnPath || `/${application}/`
        const path = nextPath.startsWith('/') ? nextPath : `/${nextPath}`

        window.history.replaceState({}, '', window.location.origin + path)

        const factor = response.data || {}

        if (factor.twoFactorSetup || factor.twoFactorRequired) {

          this.oauthFactor = {
            phase: factor.twoFactorSetup ? 'setup' : 'challenge',
            setupToken: factor.setupToken || '',
            challengeToken: factor.challengeToken || '',
            secret: factor.secret || '',
            otpauthUrl: factor.otpauthUrl || '',
            recoveryCodes: [],
            error: '',
            busy: false,
            username: '',
            userSession: null
          }

          return
        }

        writeLoggedInUser(response.data.username, {
          account: this.routeAccount,
          application: this.$route.params.application
        })
        this.loggedInUser = response.data.username

        window.dispatchEvent(new CustomEvent('superturbo-login', {
          detail: {
            username: response.data.username,
            userSession: response.data.userSession
          }
        }))

        console.log('oauth login successful:', response.data.username)
      } catch (error) {
        console.log('oauth token exchange failed:', error)
      }
    }
  },
  data () {
    return {
      appName: 'TurboHammer',
      pageTitle: '',
      components: [],
      uniqueTitle: ``,
      menuItems: [],
      menuValues: {},
      customLogo: null,
      loggedInUser: readLoggedInUser(),
      loginRequired: false,
      permissionDenied: false,
      pageOpenBanners: [],
      embedNotice: '',
      isPreviewMode: false,
      breadcrumbItems: [],
      breadcrumbLoadSeq: 0,
      pageLoadSeq: 0,
      accountLocked: false,
      appVisible: false,
      customCodeLoaded: false,
      modalOpen: false,
      modalTarget: null,
      modalUniqueTitles: [],
      modalHomeUniqueTitle: '',
      modalRedirected: false,
      modalOpener: null,
      userSession: null,
      sessionNotice: '',
      sessionWarning: false,
      passwordReset: false,
      sessionWatch: null,
      sessionPollTimer: null,
      sessionActivity: null,
      signingOut: false,
      oauthFactor: null
    }
  },
  mounted () {
    this._modalNavigation = (to, from) => this.onModalNavigation(to, from)
    this._onDocumentLinkClick = (event) => this.onDocumentLinkClick(event)
    window.__superturbo_modalNavigation = this._modalNavigation
    document.addEventListener('click', this._onDocumentLinkClick, true)

    if (this.routePageUniqueTitle === '__preview__') {
      this.appVisible = true
      this.isPreviewMode = true
      window.__superturbo_preview_mode = true
      this.loadTheme()
      this.loadMenu()
      this.loadAppCss()
      this.preparePreview()
    } else {
      this.initApp()
    }

    window.addEventListener('superturbo-close-modal', (event) => {

      const reload = !!(event.detail && event.detail.reload)

      if (this.modalOpen) this.closePageModal()

      if (reload) this.reloadCurrentPage()
    })

    window.addEventListener('superturbo-login', (event) => {

      this.sessionNotice = ''
      this.loggedInUser = event.detail.username

      if (this.modalOpen) this.closePageModal()
      this.modalRedirected = false

      if (event.detail && event.detail.userSession) {

        this.userSession = userSessionForRead(event.detail.userSession)
      }

      this.startSessionWatch()
      this.loadMenu()
      this.loadMenuValues()
      this.reloadPageForLoginState()
    })

    window.addEventListener('superturbo-session-ended', (event) => {

      const reason = event.detail && event.detail.reason

      this.signout({ reason, revoke: false })
    })
  },
  beforeUnmount () {
    this.stopSessionWatch()
    document.removeEventListener('click', this._onDocumentLinkClick, true)
    if (window.__superturbo_modalNavigation === this._modalNavigation) {
      window.__superturbo_modalNavigation = null
    }
    if (this.modalOpen) document.body.style.overflow = ''
    if (this.isPreviewMode) {
      window.removeEventListener('message', this.onPreviewMessage)
    }
  }
}
</script>

<style scoped>
.app-logo {
  max-width: 32px;
  max-height: 32px;
}

.embed-notice {
  margin: 1.25rem auto 0;
  max-width: 36rem;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
}

.preview-navbar :deep(.nav-link),
.preview-navbar :deep(.navbar-brand),
.preview-navbar :deep(.dropdown-item) {
  pointer-events: none;
}

/* Below the md breakpoint the account control lives in the opened menu.
   App CSS often paints every .btn, which turned this toggle into a small
   chip sitting on the hamburger. Keep it a full-width row, and keep its
   menu in the flow so it cannot cover the toggle. */
@media (max-width: 767.98px) {
  .navbar :deep(.navbar-collapse) {
    margin-top: 0.35rem;
    padding-top: 0.35rem;
    border-top: 1px solid rgba(var(--bs-emphasis-color-rgb), 0.2);
  }

  .navbar :deep(.navbar-collapse > .navbar-nav) {
    align-items: stretch;
    width: 100%;
  }

  .navbar :deep(.navbar-collapse .nav-item),
  .navbar :deep(.navbar-collapse .dropdown),
  .navbar :deep(.navbar-collapse .nav-link) {
    width: 100%;
    text-align: left;
  }

  .navbar :deep(.navbar-account .dropdown-toggle) {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 0;
    padding: 0.45rem 0.5rem;
    border: 0 !important;
    border-radius: 0.25rem;
    background-color: transparent !important;
    box-shadow: none !important;
    color: var(--bs-navbar-brand-color, #fff) !important;
    font-style: normal;
  }

  .navbar :deep(.navbar-account .dropdown-toggle:hover),
  .navbar :deep(.navbar-account .dropdown-toggle:focus) {
    background-color: rgba(var(--bs-emphasis-color-rgb), 0.12) !important;
    color: var(--bs-navbar-hover-color) !important;
  }

  .navbar :deep(.navbar-nav .dropdown-menu) {
    position: static !important;
    transform: none !important;
    inset: auto !important;
    width: 100% !important;
    margin: 0;
    padding: 0 0 0.25rem 0.75rem;
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .navbar :deep(.navbar-nav .dropdown-item) {
    color: var(--bs-navbar-brand-color, #fff);
    border-radius: 0.25rem;
  }

  .navbar :deep(.navbar-nav .dropdown-item:hover),
  .navbar :deep(.navbar-nav .dropdown-item:focus) {
    background: rgba(var(--bs-emphasis-color-rgb), 0.12);
    color: var(--bs-navbar-hover-color);
  }
}

.superturbo-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  text-align: left;
  list-style: none;
}

.breadcrumb-link {
  color: var(--bs-link-color, #0d6efd);
  text-decoration: none;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-current {
  color: var(--bs-secondary-color, #6c757d);
}

.breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

/* App CSS often paints every heading white and only fills a bar behind
   headings that are direct children of the page container. These messages
   sit one level down, so they inherit the page color instead. */
.access-gate h1 {
  color: inherit;
}

.skip-link {
  position: absolute;
  left: 8px;
  top: -48px;
  z-index: 2000;
  background: #fff;
  color: #111;
  padding: 8px 12px;
  border-radius: 4px;
}

.skip-link:focus {
  top: 8px;
}

.session-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.75rem 1rem 0;
}

.oauth-factor-panel {
  max-width: 28rem;
  margin: 1.5rem auto;
  padding: 1.25rem;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
